import { useEffect, useState } from "react";
import type { GoodsData } from "@laftel-ip-originals/notion/contracts";
import { getOriginalBySlug } from "~/data/ip";
import { getGoodsPath } from "~/data/content-paths";
import { fetchStaticJson, getCachedJson, preloadJson } from "~/lib/json-cache";

interface GoodsState {
  data: GoodsData | null;
  error?: string;
  isLoading: boolean;
}

export function useGoods(slug: string | undefined): GoodsState {
  const path = slug && getOriginalBySlug(slug) ? getGoodsPath(slug) : null;

  const [state, setState] = useState<GoodsState>(() => {
    if (!path) return { data: null, isLoading: false };

    preloadJson(path);
    const cached = getCachedJson<GoodsData>(path);
    if (cached) return { data: cached, isLoading: false };

    return { data: null, isLoading: true };
  });

  useEffect(() => {
    if (!path) {
      setState({ data: null, isLoading: false });
      return;
    }

    const cached = getCachedJson<GoodsData>(path);
    if (cached) {
      setState({ data: cached, isLoading: false });
      return;
    }

    let cancelled = false;
    setState({ data: null, isLoading: true });

    fetchStaticJson<GoodsData>(path)
      .then((data) => {
        if (!cancelled) setState({ data, isLoading: false });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            data: null,
            error: error instanceof Error ? error.message : "Failed to load goods.",
            isLoading: false,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [path]);

  return state;
}
